import React, { useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useToast } from '../contexts/ToastProvider';
import { MOCK_IMAGE_SETS, MOCK_SUPERSETS } from '../schemas/mockData';
import type { ExerciseDefinition, ImageSet, Superset } from '../schemas/types';
import { SaveIcon, SendIcon, AlertCircleIcon, CheckCircleIcon, PlayIcon, XCircleIcon, InfoIcon, PlusIcon, TrashIcon, GripVerticalIcon, UploadIcon, SparklesIcon, FileTextIcon, ArrowRightIcon, ArrowLeftIcon } from 'lucide-react';
type SurveyType = 'phq9' | 'gad7' | 'custom' | 'none';
type SurveyPosition = 'prepend' | 'append';
type SurveyMode = 'internal' | 'external';
interface CustomQuestion {
  id: string;
  text: string;
  type: 'multiple-choice' | 'scale';
  options?: string[];
  scaleMin?: number;
  scaleMax?: number;
  scaleLabels?: {
    min: string;
    max: string;
  };
}
interface ExternalSurveyConfig {
  url: string;
  idParameter: string;
  returnUrl?: string;
}
interface AIGeneratedQuestion {
  id: string;
  text: string;
  type: 'multiple-choice' | 'scale';
  options?: string[];
  scaleMin?: number;
  scaleMax?: number;
  scaleLabels?: {
    min: string;
    max: string;
  };
}
export function ExerciseAuthoring() {
  const {
    t
  } = useOrganization();
  const {
    showToast
  } = useToast();
  const [useSuperset, setUseSuperset] = useState(false);
  const [selectedSuperset, setSelectedSuperset] = useState<string>('');
  const [selectedSetA, setSelectedSetA] = useState<string>('');
  const [selectedSetB, setSelectedSetB] = useState<string>('');
  const [showPreflightCheck, setShowPreflightCheck] = useState(false);
  const [preflightResult, setPreflightResult] = useState<any>(null);
  // Ancillary exercise state
  const [ancillaryEnabled, setAncillaryEnabled] = useState(false);
  const [surveyMode, setSurveyMode] = useState<SurveyMode>('internal');
  const [surveyType, setSurveyType] = useState<SurveyType>('none');
  const [surveyPosition, setSurveyPosition] = useState<SurveyPosition>('prepend');
  const [customQuestions, setCustomQuestions] = useState<CustomQuestion[]>([]);
  const [externalSurvey, setExternalSurvey] = useState<ExternalSurveyConfig>({
    url: '',
    idParameter: 'participant_id',
    returnUrl: ''
  });
  // AI Integration state
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<AIGeneratedQuestion[]>([]);
  const [showGeneratedResults, setShowGeneratedResults] = useState(false);
  const handleRunPreflightCheck = async () => {
    setShowPreflightCheck(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    const mockResult = {
      status: 'warning' as const,
      estimatedSessions: 8,
      warnings: ['Self/Foundational: Only 22/30 images available for 10 sessions', 'Material/Experiential: Only 25/30 images available for 10 sessions'],
      errors: [],
      capacityOk: true
    };
    setPreflightResult(mockResult);
  };
  const getStatusColor = (status: 'pass' | 'warning' | 'fail') => {
    switch (status) {
      case 'pass':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'fail':
        return 'text-red-600 bg-red-50 border-red-200';
    }
  };
  const getStatusIcon = (status: 'pass' | 'warning' | 'fail') => {
    switch (status) {
      case 'pass':
        return <CheckCircleIcon className="w-5 h-5" data-id="element-852" />;
      case 'warning':
        return <AlertCircleIcon className="w-5 h-5" data-id="element-853" />;
      case 'fail':
        return <XCircleIcon className="w-5 h-5" data-id="element-854" />;
    }
  };
  const addCustomQuestion = () => {
    const newQuestion: CustomQuestion = {
      id: `q-${Date.now()}`,
      text: '',
      type: 'multiple-choice',
      options: ['Option 1', 'Option 2', 'Option 3']
    };
    setCustomQuestions([...customQuestions, newQuestion]);
  };
  const removeCustomQuestion = (id: string) => {
    setCustomQuestions(customQuestions.filter(q => q.id !== id));
  };
  const updateCustomQuestion = (id: string, updates: Partial<CustomQuestion>) => {
    setCustomQuestions(customQuestions.map(q => q.id === id ? {
      ...q,
      ...updates
    } : q));
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      showToast(`File "${file.name}" uploaded successfully`, 'success');
    } else {
      showToast('Please upload a PDF file', 'error');
    }
  };
  const handleGenerateSurvey = async () => {
    if (!uploadedFile && !aiPrompt) {
      showToast('Please upload a file or enter a prompt', 'error');
      return;
    }
    setIsGenerating(true);
    setShowGeneratedResults(false);
    // Simulate AI processing
    await new Promise(resolve => setTimeout(resolve, 2500));
    // Generate mock questions based on prompt
    const mockQuestions: AIGeneratedQuestion[] = [{
      id: `ai-q-${Date.now()}-1`,
      text: 'Over the last 2 weeks, how often have you been bothered by feeling down, depressed, or hopeless?',
      type: 'multiple-choice',
      options: ['Not at all', 'Several days', 'More than half the days', 'Nearly every day']
    }, {
      id: `ai-q-${Date.now()}-2`,
      text: 'Over the last 2 weeks, how often have you had little interest or pleasure in doing things?',
      type: 'multiple-choice',
      options: ['Not at all', 'Several days', 'More than half the days', 'Nearly every day']
    }, {
      id: `ai-q-${Date.now()}-3`,
      text: 'How would you rate your overall mood today?',
      type: 'scale',
      scaleMin: 0,
      scaleMax: 10,
      scaleLabels: {
        min: 'Very Poor',
        max: 'Excellent'
      }
    }, {
      id: `ai-q-${Date.now()}-4`,
      text: 'Over the last 2 weeks, how often have you had trouble falling or staying asleep?',
      type: 'multiple-choice',
      options: ['Not at all', 'Several days', 'More than half the days', 'Nearly every day']
    }, {
      id: `ai-q-${Date.now()}-5`,
      text: 'How confident do you feel about managing your mental health?',
      type: 'scale',
      scaleMin: 1,
      scaleMax: 5,
      scaleLabels: {
        min: 'Not Confident',
        max: 'Very Confident'
      }
    }];
    setGeneratedQuestions(mockQuestions);
    setShowGeneratedResults(true);
    setIsGenerating(false);
    showToast('Survey generated successfully!', 'success');
  };
  const handleApplyGeneratedQuestions = (position: 'prepend' | 'append') => {
    const convertedQuestions: CustomQuestion[] = generatedQuestions.map(q => ({
      ...q,
      id: `custom-${Date.now()}-${Math.random()}`
    }));
    if (position === 'prepend') {
      setCustomQuestions([...convertedQuestions, ...customQuestions]);
    } else {
      setCustomQuestions([...customQuestions, ...convertedQuestions]);
    }
    setSurveyType('custom');
    setShowAIGenerator(false);
    setGeneratedQuestions([]);
    setShowGeneratedResults(false);
    setUploadedFile(null);
    setAiPrompt('');
    showToast(`${convertedQuestions.length} questions added to survey`, 'success');
  };
  return <div className="p-8" data-id="element-855">
      <div className="mb-8" data-id="element-856">
        <h1 className="text-3xl font-semibold text-gray-900" data-id="element-857">{t.title}</h1>
        <p className="text-gray-600 mt-1" data-id="element-858">{t.subtitle}</p>
      </div>
      <div className="max-w-4xl" data-id="element-859">
        {/* Info Banner */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-id="element-860">
          <InfoIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-861" />
          <div className="text-sm text-blue-800" data-id="element-862">
            <strong data-id="element-863">Exercise Creation Flow:</strong> First, author your exercise
            by selecting image sets. Then, run a preflight check to validate
            capacity. Finally, proceed to publish to generate the embed code and
            activation link.
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" data-id="element-864">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-865">
            {t.basicInfo}
          </h2>
          <div className="space-y-4" data-id="element-866">
            <div data-id="element-867">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-868">
                {t.exerciseId}
              </label>
              <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="EX-EMO-UK-2025-10-v1" data-id="element-869" />
            </div>
            <div data-id="element-870">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-871">
                {t.exerciseTitle}
              </label>
              <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Emotional State Assessment" data-id="element-872" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" data-id="element-873">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-874">
            {t.imageSets}
          </h2>
          {/* Selection Mode Toggle */}
          <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-875">
            <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-876">
              Choose Image Set Configuration
            </label>
            <div className="grid grid-cols-2 gap-4" data-id="element-877">
              <button onClick={() => setUseSuperset(false)} className={`p-4 rounded-lg border-2 transition-all ${!useSuperset ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-878">
                <div className="flex items-center gap-2 mb-2" data-id="element-879">
                  <input type="radio" checked={!useSuperset} onChange={() => setUseSuperset(false)} className="w-4 h-4 text-blue-600" data-id="element-880" />
                  <span className="font-semibold text-gray-900" data-id="element-881">
                    Individual Sets
                  </span>
                </div>
                <p className="text-xs text-gray-600" data-id="element-882">
                  Select Set A and Set B individually. Best for single-session
                  assessments.
                </p>
              </button>
              <button onClick={() => setUseSuperset(true)} className={`p-4 rounded-lg border-2 transition-all ${useSuperset ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-883">
                <div className="flex items-center gap-2 mb-2" data-id="element-884">
                  <input type="radio" checked={useSuperset} onChange={() => setUseSuperset(true)} className="w-4 h-4 text-blue-600" data-id="element-885" />
                  <span className="font-semibold text-gray-900" data-id="element-886">Superset</span>
                </div>
                <p className="text-xs text-gray-600" data-id="element-887">
                  Use a superset with no-repeat policies. Best for longitudinal
                  studies.
                </p>
              </button>
            </div>
          </div>
          {useSuperset ? <div className="space-y-4" data-id="element-888">
              <div data-id="element-889">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-890">
                  {t.selectSuperset}
                </label>
                <select value={selectedSuperset} onChange={e => setSelectedSuperset(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-891">
                  <option value="" data-id="element-892">{t.selectSet}</option>
                  {MOCK_SUPERSETS.map(superset => <option key={superset.id} value={superset.id} data-id="element-893">
                      {superset.name}
                    </option>)}
                </select>
                {selectedSuperset && <p className="text-xs text-gray-500 mt-2" data-id="element-894">
                    This superset includes{' '}
                    {MOCK_SUPERSETS.find(s => s.id === selectedSuperset)?.setIds.length}{' '}
                    image sets with no-repeat policies enabled.
                  </p>}
              </div>
              {selectedSuperset && <button onClick={handleRunPreflightCheck} className="w-full px-4 py-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-2 font-medium" data-id="element-895">
                  <PlayIcon className="w-4 h-4" data-id="element-896" />
                  {t.runPreflightCheck}
                </button>}
            </div> : <div className="space-y-4" data-id="element-897">
              <div className="grid grid-cols-2 gap-4" data-id="element-898">
                <div data-id="element-899">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-900">
                    {t.setA}
                  </label>
                  <select value={selectedSetA} onChange={e => setSelectedSetA(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-901">
                    <option value="" data-id="element-902">{t.selectSet}</option>
                    {MOCK_IMAGE_SETS.map(set => <option key={set.id} value={set.id} data-id="element-903">
                        {set.name}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-904">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-905">
                    {t.setB}
                  </label>
                  <select value={selectedSetB} onChange={e => setSelectedSetB(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-906">
                    <option value="" data-id="element-907">{t.selectSet}</option>
                    {MOCK_IMAGE_SETS.map(set => <option key={set.id} value={set.id} data-id="element-908">
                        {set.name}
                      </option>)}
                  </select>
                </div>
              </div>
              {selectedSetA && selectedSetB && <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2" data-id="element-909">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-910" />
                  <span className="text-sm text-green-800" data-id="element-911">
                    Both sets selected. You can now save and continue to
                    publish.
                  </span>
                </div>}
            </div>}
        </div>
        {/* Ancillary Exercises Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" data-id="element-912">
          <div className="flex items-center justify-between mb-4" data-id="element-913">
            <div data-id="element-914">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-915">
                Ancillary Exercises
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-916">
                Add supplementary surveys before or after the main assessment
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-id="element-917">
              <input type="checkbox" checked={ancillaryEnabled} onChange={e => setAncillaryEnabled(e.target.checked)} className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" data-id="element-918" />
              <span className="text-sm font-medium text-gray-700" data-id="element-919">
                Enable Ancillary Exercises
              </span>
            </label>
          </div>
          {ancillaryEnabled && <div className="space-y-6 pt-4 border-t border-gray-200" data-id="element-920">
              {/* Survey Mode Selection */}
              <div data-id="element-921">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-922">
                  Survey Mode
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-923">
                  <button onClick={() => setSurveyMode('internal')} className={`p-4 rounded-lg border-2 transition-all text-left ${surveyMode === 'internal' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-924">
                    <div className="flex items-center gap-2 mb-1" data-id="element-925">
                      <input type="radio" checked={surveyMode === 'internal'} onChange={() => setSurveyMode('internal')} className="w-4 h-4 text-blue-600" data-id="element-926" />
                      <span className="font-semibold text-gray-900" data-id="element-927">
                        Internal Survey
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 ml-6" data-id="element-928">
                      Use built-in surveys (PHQ-9, GAD-7, or custom questions)
                    </p>
                  </button>
                  <button onClick={() => setSurveyMode('external')} className={`p-4 rounded-lg border-2 transition-all text-left ${surveyMode === 'external' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-929">
                    <div className="flex items-center gap-2 mb-1" data-id="element-930">
                      <input type="radio" checked={surveyMode === 'external'} onChange={() => setSurveyMode('external')} className="w-4 h-4 text-blue-600" data-id="element-931" />
                      <span className="font-semibold text-gray-900" data-id="element-932">
                        External Link
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 ml-6" data-id="element-933">
                      Redirect to an external survey platform (Qualtrics,
                      SurveyMonkey, etc.)
                    </p>
                  </button>
                </div>
              </div>
              {/* AI Generator Toggle Button */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg" data-id="element-934">
                <div className="flex items-center gap-3" data-id="element-935">
                  <SparklesIcon className="w-6 h-6 text-purple-600" data-id="element-936" />
                  <div data-id="element-937">
                    <h3 className="font-semibold text-gray-900" data-id="element-938">
                      AI Survey Generator
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-939">
                      Generate custom surveys from PDFs using AI
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowAIGenerator(!showAIGenerator)} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium" data-id="element-940">
                  {showAIGenerator ? 'Hide Generator' : 'Open Generator'}
                </button>
              </div>
              {/* AI Generator Interface */}
              {showAIGenerator && <div className="bg-gray-50 border-2 border-purple-200 rounded-lg p-6 space-y-4" data-id="element-941">
                  <div className="flex items-center gap-2 mb-4" data-id="element-942">
                    <SparklesIcon className="w-5 h-5 text-purple-600" data-id="element-943" />
                    <h3 className="font-semibold text-gray-900" data-id="element-944">
                      AI-Powered Survey Generation
                    </h3>
                  </div>
                  {/* File Upload */}
                  <div data-id="element-945">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-946">
                      Upload Reference Document (Optional)
                    </label>
                    <div className="relative" data-id="element-947">
                      <input type="file" accept=".pdf" onChange={handleFileUpload} className="hidden" id="pdf-upload" data-id="element-948" />
                      <label htmlFor="pdf-upload" className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors cursor-pointer" data-id="element-949">
                        <UploadIcon className="w-5 h-5 text-gray-400" data-id="element-950" />
                        <div className="flex-1" data-id="element-951">
                          {uploadedFile ? <div className="flex items-center gap-2" data-id="element-952">
                              <FileTextIcon className="w-4 h-4 text-purple-600" data-id="element-953" />
                              <span className="text-sm font-medium text-gray-900" data-id="element-954">
                                {uploadedFile.name}
                              </span>
                              <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-955" />
                            </div> : <span className="text-sm text-gray-600" data-id="element-956">
                              Click to upload PDF (e.g., PHQ-9.pdf, GAD-7.pdf)
                            </span>}
                        </div>
                      </label>
                    </div>
                  </div>
                  {/* Prompt Input */}
                  <div data-id="element-957">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-958">
                      Generation Prompt
                    </label>
                    <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder="Example: Generate a depression survey based on this PDF with 5 questions using a 4-point scale" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none" rows={4} data-id="element-959" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-960">
                      Describe what kind of survey you want to generate. Be
                      specific about question types, scales, and focus areas.
                    </p>
                  </div>
                  {/* Generate Button */}
                  <button onClick={handleGenerateSurvey} disabled={isGenerating || !uploadedFile && !aiPrompt} className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-961">
                    {isGenerating ? <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" data-id="element-962" />
                        Generating Survey...
                      </> : <>
                        <SparklesIcon className="w-5 h-5" data-id="element-963" />
                        Generate Survey
                      </>}
                  </button>
                  {/* Generated Results */}
                  {showGeneratedResults && generatedQuestions.length > 0 && <div className="mt-6 p-4 bg-white border-2 border-green-200 rounded-lg" data-id="element-964">
                      <div className="flex items-center gap-2 mb-4" data-id="element-965">
                        <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-966" />
                        <h4 className="font-semibold text-gray-900" data-id="element-967">
                          Generated Survey ({generatedQuestions.length}{' '}
                          questions)
                        </h4>
                      </div>
                      <div className="space-y-3 mb-4 max-h-96 overflow-y-auto" data-id="element-968">
                        {generatedQuestions.map((question, index) => <div key={question.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-969">
                            <div className="flex items-start gap-2" data-id="element-970">
                              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-semibold" data-id="element-971">
                                {index + 1}
                              </span>
                              <div className="flex-1" data-id="element-972">
                                <p className="text-sm font-medium text-gray-900 mb-2" data-id="element-973">
                                  {question.text}
                                </p>
                                {question.type === 'multiple-choice' && question.options && <div className="flex flex-wrap gap-2" data-id="element-974">
                                      {question.options.map((option, optIndex) => <span key={optIndex} className="px-2 py-1 bg-white border border-gray-300 rounded text-xs text-gray-700" data-id="element-975">
                                            {option}
                                          </span>)}
                                    </div>}
                                {question.type === 'scale' && <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-976">
                                    <span data-id="element-977">
                                      Scale: {question.scaleMin} -{' '}
                                      {question.scaleMax}
                                    </span>
                                    {question.scaleLabels && <span className="text-gray-500" data-id="element-978">
                                        ({question.scaleLabels.min} →{' '}
                                        {question.scaleLabels.max})
                                      </span>}
                                  </div>}
                              </div>
                            </div>
                          </div>)}
                      </div>
                      {/* Apply Buttons */}
                      <div className="flex gap-3" data-id="element-979">
                        <button onClick={() => handleApplyGeneratedQuestions('prepend')} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2" data-id="element-980">
                          <ArrowLeftIcon className="w-4 h-4" data-id="element-981" />
                          Add to Beginning
                        </button>
                        <button onClick={() => handleApplyGeneratedQuestions('append')} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2" data-id="element-982">
                          Add to End
                          <ArrowRightIcon className="w-4 h-4" data-id="element-983" />
                        </button>
                      </div>
                    </div>}
                </div>}
              {/* Internal Survey Configuration */}
              {surveyMode === 'internal' && <>
                  {/* Survey Type Selection */}
                  <div data-id="element-984">
                    <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-985">
                      Survey Type
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-986">
                      <button onClick={() => setSurveyType('phq9')} className={`p-4 rounded-lg border-2 transition-all text-left ${surveyType === 'phq9' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-987">
                        <div className="flex items-center gap-2 mb-1" data-id="element-988">
                          <input type="radio" checked={surveyType === 'phq9'} onChange={() => setSurveyType('phq9')} className="w-4 h-4 text-blue-600" data-id="element-989" />
                          <span className="font-semibold text-gray-900" data-id="element-990">
                            PHQ-9
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 ml-6" data-id="element-991">
                          Patient Health Questionnaire (9 items)
                        </p>
                      </button>
                      <button onClick={() => setSurveyType('gad7')} className={`p-4 rounded-lg border-2 transition-all text-left ${surveyType === 'gad7' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-992">
                        <div className="flex items-center gap-2 mb-1" data-id="element-993">
                          <input type="radio" checked={surveyType === 'gad7'} onChange={() => setSurveyType('gad7')} className="w-4 h-4 text-blue-600" data-id="element-994" />
                          <span className="font-semibold text-gray-900" data-id="element-995">
                            GAD-7
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 ml-6" data-id="element-996">
                          Generalized Anxiety Disorder (7 items)
                        </p>
                      </button>
                      <button onClick={() => setSurveyType('custom')} className={`p-4 rounded-lg border-2 transition-all text-left col-span-2 ${surveyType === 'custom' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-997">
                        <div className="flex items-center gap-2 mb-1" data-id="element-998">
                          <input type="radio" checked={surveyType === 'custom'} onChange={() => setSurveyType('custom')} className="w-4 h-4 text-blue-600" data-id="element-999" />
                          <span className="font-semibold text-gray-900" data-id="element-1000">
                            Custom Survey
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 ml-6" data-id="element-1001">
                          Create your own questions with multiple-choice or
                          scale responses
                        </p>
                      </button>
                    </div>
                  </div>
                  {/* Position Selection */}
                  {surveyType !== 'none' && <div data-id="element-1002">
                      <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1003">
                        Survey Position
                      </label>
                      <div className="flex gap-3" data-id="element-1004">
                        <button onClick={() => setSurveyPosition('prepend')} className={`flex-1 p-3 rounded-lg border-2 transition-all ${surveyPosition === 'prepend' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1005">
                          <div className="flex items-center gap-2" data-id="element-1006">
                            <input type="radio" checked={surveyPosition === 'prepend'} onChange={() => setSurveyPosition('prepend')} className="w-4 h-4 text-blue-600" data-id="element-1007" />
                            <span className="font-medium text-gray-900" data-id="element-1008">
                              Before Main Exercise
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1 ml-6" data-id="element-1009">
                            Survey → Image Assessment
                          </p>
                        </button>
                        <button onClick={() => setSurveyPosition('append')} className={`flex-1 p-3 rounded-lg border-2 transition-all ${surveyPosition === 'append' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1010">
                          <div className="flex items-center gap-2" data-id="element-1011">
                            <input type="radio" checked={surveyPosition === 'append'} onChange={() => setSurveyPosition('append')} className="w-4 h-4 text-blue-600" data-id="element-1012" />
                            <span className="font-medium text-gray-900" data-id="element-1013">
                              After Main Exercise
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1 ml-6" data-id="element-1014">
                            Image Assessment → Survey
                          </p>
                        </button>
                      </div>
                    </div>}
                  {/* Custom Questions Editor */}
                  {surveyType === 'custom' && <div data-id="element-1015">
                      <div className="flex items-center justify-between mb-3" data-id="element-1016">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-1017">
                          Custom Questions
                        </label>
                        <button onClick={addCustomQuestion} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-1018">
                          <PlusIcon className="w-4 h-4" data-id="element-1019" />
                          Add Question
                        </button>
                      </div>
                      {customQuestions.length === 0 ? <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-1020">
                          <p className="text-sm text-gray-600 mb-2" data-id="element-1021">
                            No questions added yet
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1022">
                            Click "Add Question" to create your first question
                          </p>
                        </div> : <div className="space-y-4" data-id="element-1023">
                          {customQuestions.map((question, index) => <div key={question.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1024">
                              <div className="flex items-start gap-3" data-id="element-1025">
                                <GripVerticalIcon className="w-5 h-5 text-gray-400 mt-2 flex-shrink-0" data-id="element-1026" />
                                <div className="flex-1 space-y-3" data-id="element-1027">
                                  <div className="flex items-start gap-3" data-id="element-1028">
                                    <div className="flex-1" data-id="element-1029">
                                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1030">
                                        Question {index + 1}
                                      </label>
                                      <input type="text" value={question.text} onChange={e => updateCustomQuestion(question.id, {
                            text: e.target.value
                          })} placeholder="Enter your question..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1031" />
                                    </div>
                                    <div className="w-40" data-id="element-1032">
                                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1033">
                                        Type
                                      </label>
                                      <select value={question.type} onChange={e => updateCustomQuestion(question.id, {
                            type: e.target.value as 'multiple-choice' | 'scale'
                          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1034">
                                        <option value="multiple-choice" data-id="element-1035">
                                          Multiple Choice
                                        </option>
                                        <option value="scale" data-id="element-1036">Scale</option>
                                      </select>
                                    </div>
                                  </div>
                                  {question.type === 'multiple-choice' && <div data-id="element-1037">
                                      <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-1038">
                                        Answer Options
                                      </label>
                                      <div className="space-y-2" data-id="element-1039">
                                        {question.options?.map((option, optIndex) => <input key={optIndex} type="text" value={option} onChange={e => {
                            const newOptions = [...(question.options || [])];
                            newOptions[optIndex] = e.target.value;
                            updateCustomQuestion(question.id, {
                              options: newOptions
                            });
                          }} placeholder={`Option ${optIndex + 1}`} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1040" />)}
                                      </div>
                                    </div>}
                                  {question.type === 'scale' && <div className="grid grid-cols-2 gap-3" data-id="element-1041">
                                      <div data-id="element-1042">
                                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1043">
                                          Scale Range
                                        </label>
                                        <div className="flex gap-2" data-id="element-1044">
                                          <input type="number" value={question.scaleMin || 0} onChange={e => updateCustomQuestion(question.id, {
                              scaleMin: parseInt(e.target.value)
                            })} placeholder="Min" className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1045" />
                                          <span className="text-gray-500 self-center" data-id="element-1046">
                                            to
                                          </span>
                                          <input type="number" value={question.scaleMax || 10} onChange={e => updateCustomQuestion(question.id, {
                              scaleMax: parseInt(e.target.value)
                            })} placeholder="Max" className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1047" />
                                        </div>
                                      </div>
                                      <div data-id="element-1048">
                                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1049">
                                          Scale Labels (Optional)
                                        </label>
                                        <div className="flex gap-2" data-id="element-1050">
                                          <input type="text" value={question.scaleLabels?.min || ''} onChange={e => updateCustomQuestion(question.id, {
                              scaleLabels: {
                                ...question.scaleLabels,
                                min: e.target.value,
                                max: question.scaleLabels?.max || ''
                              }
                            })} placeholder="Low" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1051" />
                                          <input type="text" value={question.scaleLabels?.max || ''} onChange={e => updateCustomQuestion(question.id, {
                              scaleLabels: {
                                min: question.scaleLabels?.min || '',
                                max: e.target.value
                              }
                            })} placeholder="High" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1052" />
                                        </div>
                                      </div>
                                    </div>}
                                </div>
                                <button onClick={() => removeCustomQuestion(question.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0" data-id="element-1053">
                                  <TrashIcon className="w-4 h-4" data-id="element-1054" />
                                </button>
                              </div>
                            </div>)}
                        </div>}
                    </div>}
                  {/* Standard Survey Preview */}
                  {(surveyType === 'phq9' || surveyType === 'gad7') && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1055">
                      <div className="flex items-start gap-2" data-id="element-1056">
                        <InfoIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1057" />
                        <div className="text-sm text-blue-800" data-id="element-1058">
                          <strong data-id="element-1059">
                            {surveyType === 'phq9' ? 'PHQ-9' : 'GAD-7'} Survey:
                          </strong>{' '}
                          This standardized survey will be administered{' '}
                          {surveyPosition === 'prepend' ? 'before' : 'after'}{' '}
                          the main image assessment. The survey includes{' '}
                          {surveyType === 'phq9' ? '9' : '7'} questions with a
                          4-point Likert scale (0-3).
                        </div>
                      </div>
                    </div>}
                </>}
              {/* External Survey Configuration */}
              {surveyMode === 'external' && <div className="space-y-4" data-id="element-1060">
                  {/* Position Selection for External */}
                  <div data-id="element-1061">
                    <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1062">
                      Survey Position
                    </label>
                    <div className="flex gap-3" data-id="element-1063">
                      <button onClick={() => setSurveyPosition('prepend')} className={`flex-1 p-3 rounded-lg border-2 transition-all ${surveyPosition === 'prepend' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1064">
                        <div className="flex items-center gap-2" data-id="element-1065">
                          <input type="radio" checked={surveyPosition === 'prepend'} onChange={() => setSurveyPosition('prepend')} className="w-4 h-4 text-blue-600" data-id="element-1066" />
                          <span className="font-medium text-gray-900" data-id="element-1067">
                            Before Main Exercise
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1 ml-6" data-id="element-1068">
                          External Survey → Image Assessment
                        </p>
                      </button>
                      <button onClick={() => setSurveyPosition('append')} className={`flex-1 p-3 rounded-lg border-2 transition-all ${surveyPosition === 'append' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1069">
                        <div className="flex items-center gap-2" data-id="element-1070">
                          <input type="radio" checked={surveyPosition === 'append'} onChange={() => setSurveyPosition('append')} className="w-4 h-4 text-blue-600" data-id="element-1071" />
                          <span className="font-medium text-gray-900" data-id="element-1072">
                            After Main Exercise
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1 ml-6" data-id="element-1073">
                          Image Assessment → External Survey
                        </p>
                      </button>
                    </div>
                  </div>
                  {/* External Survey URL */}
                  <div data-id="element-1074">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1075">
                      External Survey URL
                    </label>
                    <input type="url" value={externalSurvey.url} onChange={e => setExternalSurvey({
                ...externalSurvey,
                url: e.target.value
              })} placeholder="https://survey.qualtrics.com/jfe/form/SV_..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1076" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1077">
                      Full URL to your external survey (Qualtrics, SurveyMonkey,
                      Google Forms, etc.)
                    </p>
                  </div>
                  {/* ID Parameter Configuration */}
                  <div data-id="element-1078">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1079">
                      Participant ID Parameter Name
                    </label>
                    <input type="text" value={externalSurvey.idParameter} onChange={e => setExternalSurvey({
                ...externalSurvey,
                idParameter: e.target.value
              })} placeholder="participant_id" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1080" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1081">
                      The URL parameter name that will receive the participant
                      ID (e.g., "participant_id", "uid", "respondent_id")
                    </p>
                  </div>
                  {/* Return URL (Optional) */}
                  <div data-id="element-1082">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1083">
                      Return URL (Optional)
                    </label>
                    <input type="url" value={externalSurvey.returnUrl || ''} onChange={e => setExternalSurvey({
                ...externalSurvey,
                returnUrl: e.target.value
              })} placeholder="https://yoursite.com/thank-you" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1084" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1085">
                      Where participants should be redirected after completing
                      the external survey
                    </p>
                  </div>
                  {/* Preview of Generated URL */}
                  {externalSurvey.url && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1086">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1087">
                        Preview: Generated Redirect URL
                      </label>
                      <code className="block text-xs bg-white p-3 rounded border border-gray-300 break-all" data-id="element-1088">
                        {externalSurvey.url}
                        {externalSurvey.url.includes('?') ? '&' : '?'}
                        {externalSurvey.idParameter}=[PARTICIPANT_ID]
                      </code>
                      <p className="text-xs text-gray-500 mt-2" data-id="element-1089">
                        [PARTICIPANT_ID] will be automatically replaced with
                        each participant's unique identifier
                      </p>
                    </div>}
                  {/* Data Integration Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1090">
                    <div className="flex items-start gap-2" data-id="element-1091">
                      <InfoIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1092" />
                      <div className="text-sm text-blue-800" data-id="element-1093">
                        <strong data-id="element-1094">Data Integration:</strong> The participant ID
                        will be passed to your external survey via URL
                        parameter. You can use this ID to match external survey
                        responses with image assessment results post-facto. Make
                        sure your external survey platform captures this
                        parameter.
                      </div>
                    </div>
                  </div>
                  {/* Flow Diagram */}
                  <div className="bg-white border-2 border-gray-200 rounded-lg p-4" data-id="element-1095">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-1096">
                      Participant Flow
                    </h4>
                    <div className="space-y-2 text-sm" data-id="element-1097">
                      {surveyPosition === 'prepend' ? <>
                          <div className="flex items-center gap-2" data-id="element-1098">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold" data-id="element-1099">
                              1
                            </div>
                            <span className="text-gray-700" data-id="element-1100">
                              Participant redirected to external survey with ID
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-1101">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold" data-id="element-1102">
                              2
                            </div>
                            <span className="text-gray-700" data-id="element-1103">
                              Complete external survey
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-1104">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold" data-id="element-1105">
                              3
                            </div>
                            <span className="text-gray-700" data-id="element-1106">
                              Return to image assessment
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-1107">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold" data-id="element-1108">
                              4
                            </div>
                            <span className="text-gray-700" data-id="element-1109">
                              Complete image assessment
                            </span>
                          </div>
                        </> : <>
                          <div className="flex items-center gap-2" data-id="element-1110">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold" data-id="element-1111">
                              1
                            </div>
                            <span className="text-gray-700" data-id="element-1112">
                              Complete image assessment
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-1113">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold" data-id="element-1114">
                              2
                            </div>
                            <span className="text-gray-700" data-id="element-1115">
                              Redirect to external survey with ID
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-1116">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold" data-id="element-1117">
                              3
                            </div>
                            <span className="text-gray-700" data-id="element-1118">
                              Complete external survey
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-1119">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold" data-id="element-1120">
                              4
                            </div>
                            <span className="text-gray-700" data-id="element-1121">
                              {externalSurvey.returnUrl ? 'Redirect to completion page' : 'Survey complete'}
                            </span>
                          </div>
                        </>}
                    </div>
                  </div>
                </div>}
            </div>}
        </div>
        <div className="flex gap-4" data-id="element-1122">
          <button className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center justify-center gap-2" data-id="element-1123">
            <SaveIcon className="w-4 h-4" data-id="element-1124" />
            {t.saveDraft}
          </button>
          <button className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2" data-id="element-1125">
            <SendIcon className="w-4 h-4" data-id="element-1126" />
            {t.saveContinue}
          </button>
        </div>
      </div>
      {showPreflightCheck && <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowPreflightCheck(false)} data-id="element-1127" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1128">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-1129">
              <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-1130">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-1131">
                  {t.preflightCheck}
                </h2>
                <button onClick={() => setShowPreflightCheck(false)} className="p-2 hover:bg-gray-100 rounded-lg" data-id="element-1132">
                  <XCircleIcon className="w-5 h-5 text-gray-500" data-id="element-1133" />
                </button>
              </div>
              <div className="p-6 space-y-6" data-id="element-1134">
                {!preflightResult ? <div className="flex flex-col items-center gap-4 py-8" data-id="element-1135">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" data-id="element-1136" />
                    <div className="text-gray-900 font-medium" data-id="element-1137">
                      {t.checkingCapacity}
                    </div>
                  </div> : <>
                    <div className={`border-2 rounded-lg p-4 ${getStatusColor(preflightResult.status)}`} data-id="element-1138">
                      <div className="flex items-center gap-3" data-id="element-1139">
                        {getStatusIcon(preflightResult.status)}
                        <div className="flex-1" data-id="element-1140">
                          <div className="font-semibold text-lg" data-id="element-1141">
                            {t.capacityCheck}:{' '}
                            {preflightResult.status === 'pass' ? t.passed : preflightResult.status === 'warning' ? t.warning : t.failed}
                          </div>
                          <div className="text-sm mt-1" data-id="element-1142">
                            {t.estimatedSessions}:{' '}
                            <strong data-id="element-1143">{preflightResult.estimatedSessions}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                    {preflightResult.warnings.length > 0 && <div data-id="element-1144">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2" data-id="element-1145">
                          <AlertCircleIcon className="w-5 h-5 text-yellow-600" data-id="element-1146" />
                          {t.warnings}
                        </h3>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-2" data-id="element-1147">
                          {preflightResult.warnings.map((warning: string, idx: number) => <div key={idx} className="text-sm text-yellow-800" data-id="element-1148">
                                {warning}
                              </div>)}
                        </div>
                      </div>}
                    {preflightResult.errors.length > 0 && <div data-id="element-1149">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2" data-id="element-1150">
                          <XCircleIcon className="w-5 h-5 text-red-600" data-id="element-1151" />
                          {t.errors}
                        </h3>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2" data-id="element-1152">
                          {preflightResult.errors.map((error: string, idx: number) => <div key={idx} className="text-sm text-red-800" data-id="element-1153">
                                {error}
                              </div>)}
                        </div>
                      </div>}
                  </>}
              </div>
              {preflightResult && <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3" data-id="element-1154">
                  <button onClick={() => setShowPreflightCheck(false)} className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-1155">
                    {t.close}
                  </button>
                  {preflightResult.status !== 'fail' && <button onClick={() => {
              setShowPreflightCheck(false);
              showToast('Proceeding with exercise creation', 'success');
            }} className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-1156">
                      {t.proceedAnyway}
                    </button>}
                </div>}
            </div>
          </div>
        </>}
    </div>;
}